import { fetchRequest } from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";
import type { Post } from "../types";

interface ShowParams {
  spaceId: number;
  postSlug: string;
}

const show = <T>({ spaceId, postSlug }: ShowParams) =>
  fetchRequest<T>(internalApi.posts.show({ spaceId, postSlug }), {
    method: "GET",
  });

interface CreateParams {
  post: Partial<Post>;
  formData: Partial<PostFormData>;
}

const create = <T>({ post, formData }: CreateParams) =>
  fetchRequest<T>(internalApi.posts.create({ spaceId: post.space_id }), {
    method: "POST",
    body: JSON.stringify({ post: formData }),
  });

interface UpdateParams {
  post: Partial<Post>;
  formData: Partial<PostFormData>;
}

const update = <T>({ post, formData }: UpdateParams) =>
  fetchRequest<T>(
    internalApi.posts.update({ spaceId: post.space_id, postSlug: post.slug }),
    {
      method: "PATCH",
      body: JSON.stringify({ post: formData }),
    },
  );

interface PostParams {
  post: Post;
}

const destroy = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.update({ spaceId: post.space_id, postSlug: post.slug }),
    {
      method: "DELETE",
      forReactQuery: true,
    },
  );

const hideFromFeaturedAreas = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.featuredAreasVisibility.destroy({
      postSlug: post.slug,
      spaceId: post.space_id,
    }),
    {
      method: "DELETE",
      forReactQuery: true,
    },
  );

const showInFeaturedAreas = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.featuredAreasVisibility.create({
      postSlug: post.slug,
      spaceId: post.space_id,
    }),
    {
      method: "POST",
      forReactQuery: true,
    },
  );

const updateKnowledgeBasePost = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.updateKnowledgeBasePost({
      postId: String(post.id),
    }),
    {
      method: "PATCH",
    },
  );

interface PinParams {
  post?: Post;
  formData: {
    is_pinned: boolean;
    pin_to_top: boolean;
  };
}

const pin = ({ post, formData }: PinParams) =>
  fetchRequest<Post>(
    internalApi.posts.pin({ spaceId: post?.space_id, postSlug: post?.slug }),
    {
      method: "PATCH",
      body: JSON.stringify({ post: formData }),
      forReactQuery: true,
    },
  );

export interface PostFormData {
  id?: number;
  space_id?: number;
  name: string;
  tiptap_body: any;
}

interface CreateOrUpdateParams {
  post: Partial<Post>;
  formData: Partial<PostFormData>;
}

const createOrUpdate = ({ post, formData }: CreateOrUpdateParams) =>
  post.id
    ? update<Response>({ post, formData })
    : create<Response>({ post, formData });

export const postApi = {
  createOrUpdate,
  show,
  destroy,
  pin,
  hideFromFeaturedAreas,
  showInFeaturedAreas,
  updateKnowledgeBasePost,
};
